import { useState, useRef, useCallback, memo } from "react";
import { Link } from "@tanstack/react-router";
import { Plus, Upload, Trash2, ScanLine, Clock, CheckCheck, CircleDot, FileText, X, ArrowLeft, Check, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useCountingInvoices, useCreateCountingInvoice, useDeleteCountingInvoice, useProcessCountingOcr, useCreateFromOcr, isOcrAbortError, type OcrProgressState, estimateRemainingMs } from "@/hooks/use-counting";
import { toast } from "sonner";

const STATUS_CONFIG = {
  OPEN: { label: "Otvoreno", icon: Clock, class: "text-muted-foreground" },
  COUNTING: { label: "Brojanje", icon: ScanLine, class: "text-foreground" },
  DONE: { label: "Završeno", icon: CheckCheck, class: "text-green-600 dark:text-green-400" },
} as const;

export function CountingListPage() {
  const { data: invoices, isLoading } = useCountingInvoices();
  const createInvoice = useCreateCountingInvoice();
  const deleteInvoice = useDeleteCountingInvoice();
  const createFromOcr = useCreateFromOcr();

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDate, setNewDate] = useState(() => new Date().toISOString().slice(0, 10));

  const [showOcr, setShowOcr] = useState(false);
  const [ocrFile, setOcrFile] = useState<File | null>(null);
  const [ocrResult, setOcrResult] = useState<any>(null);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrProgress, setOcrProgress] = useState<OcrProgressState | null>(null);
  const [ocrStartedAt, setOcrStartedAt] = useState(0);
  const [progressTicks, setProgressTicks] = useState(0);
  const processOcr = useProcessCountingOcr((s) => {
    setOcrProgress(s);
    setProgressTicks((t) => t + 1);
  });

  async function handleCreate() {
    if (!newName.trim()) return;
    await createInvoice.mutateAsync({ name: newName.trim(), date: new Date(newDate).getTime() });
    setShowCreate(false);
    setNewName("");
    toast.success("Faktura kreirana");
  }

  async function handleOcrUpload() {
    if (!ocrFile) return;
    setOcrProcessing(true);
    setOcrProgress(null);
    setProgressTicks(0);
    setOcrStartedAt(Date.now());
    try {
      const result = await processOcr.mutateAsync(ocrFile);
      setOcrResult(result);
    } catch (e: any) {
      // Zatvaranje dijaloga je namjeran prekid, ne kvar — bez toast greške.
      if (!isOcrAbortError(e)) toast.error(e?.message || "Greška pri OCR obradi");
    } finally {
      setOcrProcessing(false);
    }
  }

  function handleOcrOpenChange(open: boolean) {
    setShowOcr(open);
    if (!open) {
      // Prekini batch-eve u letu umjesto da troše tokene za odbačen rezultat.
      if (ocrProcessing) processOcr.abort();
      setOcrResult(null);
      setOcrFile(null);
    }
  }

  async function handleOcrConfirm() {
    if (!ocrResult) return;
    try {
      await createFromOcr.mutateAsync({
        name: ocrResult.name,
        date: ocrResult.date,
        items: ocrResult.items,
        filePath: ocrResult.filePath,
      });
      setShowOcr(false);
      setOcrResult(null);
      setOcrFile(null);
      toast.success("Faktura kreirana iz OCR");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri kreiranju fakture");
    }
  }

  async function handleDelete(id: string, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm("Obrisati fakturu?")) return;
    await deleteInvoice.mutateAsync(id);
    toast.success("Obrisano");
  }

  if (isLoading) {
    return (
      <div className="p-6 flex flex-col gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 bg-muted/30 animate-pulse" />
        ))}
      </div>
    );
  }

  const grouped = {
    active: (invoices || []).filter((i: any) => i.status === "COUNTING"),
    open: (invoices || []).filter((i: any) => i.status === "OPEN"),
    done: (invoices || []).filter((i: any) => i.status === "DONE"),
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 pb-3 border-b border-foreground/5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold tracking-tight">Brojanje robe</h1>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {invoices?.length || 0} faktura ukupno
            </p>
          </div>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" onClick={() => setShowOcr(true)}>
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              OCR
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Nova
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {(!invoices || invoices.length === 0) ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="w-12 h-12 ring-1 ring-foreground/10 flex items-center justify-center mb-3">
              <CircleDot className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">Nema faktura</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Kreirajte novu fakturu ili učitajte putem OCR</p>
          </div>
        ) : (
          <div className="p-4 flex flex-col gap-5">
            {grouped.active.length > 0 && (
              <InvoiceSection title="Aktivno brojanje" invoices={grouped.active} onDelete={handleDelete} />
            )}
            {grouped.open.length > 0 && (
              <InvoiceSection title="Pripremljeno" invoices={grouped.open} onDelete={handleDelete} />
            )}
            {grouped.done.length > 0 && (
              <InvoiceSection title="Završeno" invoices={grouped.done} onDelete={handleDelete} />
            )}
          </div>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nova faktura</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Naziv</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                placeholder="npr. Faktura 123 - Dobavljač"
                autoFocus
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Datum</Label>
              <DatePicker value={newDate} onChange={setNewDate} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleCreate} disabled={!newName.trim()}>Kreiraj</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OcrDialog
        open={showOcr}
        onOpenChange={handleOcrOpenChange}
        ocrFile={ocrFile}
        setOcrFile={setOcrFile}
        ocrResult={ocrResult}
        setOcrResult={setOcrResult}
        ocrProcessing={ocrProcessing}
        ocrProgress={ocrProgress}
        ocrStartedAt={ocrStartedAt}
        progressTicks={progressTicks}
        onUpload={handleOcrUpload}
        onConfirm={handleOcrConfirm}
      />
    </div>
  );
}

function OcrDialog({
  open,
  onOpenChange,
  ocrFile,
  setOcrFile,
  ocrResult,
  setOcrResult,
  ocrProcessing,
  ocrProgress,
  ocrStartedAt,
  progressTicks,
  onUpload,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ocrFile: File | null;
  setOcrFile: (f: File | null) => void;
  ocrResult: any;
  setOcrResult: (r: any) => void;
  ocrProcessing: boolean;
  ocrProgress: OcrProgressState | null;
  ocrStartedAt: number;
  progressTicks: number;
  onUpload: () => void;
  onConfirm: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && /\.(pdf|jpe?g|png)$/i.test(file.name)) {
      setOcrFile(file);
    }
  }, [setOcrFile]);

  const totalQty = ocrResult?.items?.reduce((s: number, i: any) => s + i.quantity, 0) || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* sm:-prefixed on purpose: DialogContent's base ends with `sm:max-w-sm`,
          which Tailwind emits after unprefixed utilities — a plain `max-w-lg`
          here loses to it above 640px and the dialog stays 384px wide. */}
      <DialogContent className="w-full sm:max-w-2xl p-0 gap-0 overflow-hidden" showCloseButton={false}>
        {/* Header */}
        <div className="relative px-4 pt-4 pb-3 border-b border-foreground/[0.06]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              {ocrResult && (
                <button
                  onClick={() => setOcrResult(null)}
                  className="p-1 -ml-1 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <div>
                <h2 className="text-sm font-semibold tracking-tight">
                  {ocrResult ? "Pregled fakture" : "OCR unos"}
                </h2>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {ocrResult ? "Provjerite podatke prije kreiranja" : "Učitajte sliku ili PDF fakture"}
                </p>
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {ocrProcessing ? (
          <OcrProgressPanel
            progress={ocrProgress}
            startedAt={ocrStartedAt}
            ticks={progressTicks}
          />
        ) : !ocrResult ? (
          /* Upload state — drop zone */
          <div className="p-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={(e) => setOcrFile(e.target.files?.[0] || null)}
            />

            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`
                relative cursor-pointer group
                flex flex-col items-center justify-center
                py-10 px-4
                ring-1 transition-all duration-200
                ${dragOver
                  ? "ring-foreground/30 bg-foreground/[0.04]"
                  : ocrFile
                    ? "ring-foreground/20 bg-foreground/[0.02]"
                    : "ring-dashed ring-foreground/10 hover:ring-foreground/20 hover:bg-foreground/[0.02]"
                }
              `}
            >
              {ocrFile ? (
                <>
                  <div className="w-10 h-12 ring-1 ring-foreground/10 bg-background flex items-center justify-center mb-3">
                    <FileText className="h-5 w-5 text-foreground/60" />
                  </div>
                  <p className="text-xs font-medium truncate max-w-full">{ocrFile.name}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {(ocrFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <button
                    onClick={(e) => { e.stopPropagation(); setOcrFile(null); }}
                    className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 ring-1 ring-foreground/[0.08] flex items-center justify-center mb-3 group-hover:ring-foreground/15 transition-colors">
                    <Upload className="h-4 w-4 text-muted-foreground group-hover:text-foreground/70 transition-colors" />
                  </div>
                  <p className="text-xs font-medium">Prevucite fajl ovdje</p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    ili kliknite za odabir — PDF, JPG, PNG do 10MB
                  </p>
                </>
              )}
            </div>

            {ocrFile && (
              <Button
                className="w-full mt-3"
                onClick={onUpload}
              >
                <ScanLine className="h-3.5 w-3.5 mr-1.5" />
                Pokreni OCR
              </Button>
            )}
          </div>
        ) : (
          /* Results state — document review */
          <div className="flex flex-col min-w-0">
            {ocrResult.failedRanges?.length > 0 && (
              <div className="mx-4 mt-3 px-3 py-2 ring-1 ring-amber-500/30 bg-amber-500/[0.06]">
                <p className="text-[11px] font-medium text-amber-600 dark:text-amber-500">
                  {ocrResult.failedRanges.map((r: any) => `Stranice ${r.fromPage}–${r.toPage}`).join(", ")} nisu obrađene
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Neki artikli možda nedostaju — dodajte ih ručno nakon kreiranja.
                </p>
              </div>
            )}

            {/* Editable fields */}
            <div className="px-4 pt-3 pb-3 flex flex-col gap-2.5">
              <div className="flex gap-2.5">
                {/* min-w-0: without it the input's intrinsic ~20ch width becomes the
                    flex item's minimum, pushing the row past the dialog and clipping
                    everything to its right. */}
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Naziv</Label>
                  <Input
                    value={ocrResult.name}
                    onChange={(e) => setOcrResult({ ...ocrResult, name: e.target.value })}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="shrink-0 flex flex-col gap-1">
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Datum</Label>
                  <DatePicker
                    value={ocrResult.date}
                    onChange={(d) => setOcrResult({ ...ocrResult, date: d })}
                    className="h-8 text-xs px-2"
                  />
                </div>
              </div>
            </div>

            {/* Items list */}
            <div className="border-t border-foreground/[0.06]">
              <div className="px-4 py-2 flex items-center justify-between bg-foreground/[0.02]">
                <div className="flex items-center gap-1.5">
                  <Package className="h-3 w-3 text-muted-foreground/60" />
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-medium">
                    Artikli
                  </span>
                </div>
                <div className="flex items-center gap-3 text-[10px] tabular-nums text-muted-foreground shrink-0">
                  <span>{ocrResult.items.length} stavki</span>
                  <span className="font-medium text-foreground/80">{totalQty} kom</span>
                </div>
              </div>

              <OcrItemsList items={ocrResult.items} />
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-foreground/[0.06] flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setOcrResult(null)}>
                Ponovi skeniranje
              </Button>
              <Button size="sm" onClick={onConfirm}>
                <Check className="h-3.5 w-3.5 mr-1.5" />
                Kreiraj fakturu
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function formatEta(ms: number): string {
  const secs = Math.round(ms / 1000 / 5) * 5;
  if (secs <= 0) return "još malo…";
  if (secs < 60) return `~${secs} s`;
  return `~${Math.round(secs / 60)} min`;
}

function OcrProgressPanel({
  progress,
  startedAt,
  ticks,
}: {
  progress: OcrProgressState | null;
  startedAt: number;
  ticks: number;
}) {
  const pagesTotal = progress?.pagesTotal ?? 0;
  const pagesDone = progress?.pagesDone ?? 0;
  const indeterminate = pagesTotal <= 1;
  const pct = pagesTotal > 0
    ? Math.min(100, Math.max(0, Math.round((pagesDone / pagesTotal) * 100)))
    : 0;

  const etaMs = estimateRemainingMs(Date.now() - startedAt, pagesDone, pagesTotal);
  const showEta = ticks >= 2 && etaMs !== null;

  return (
    <div className="px-5 py-8">
      {indeterminate ? (
        <div className="h-[3px] bg-foreground/[0.08] overflow-hidden rounded-full">
          <div className="h-full w-1/3 bg-foreground/60 rounded-full animate-[shimmer_1.4s_ease-in-out_infinite]" />
        </div>
      ) : (
        <>
          <p className="text-center text-4xl font-semibold tracking-tight tabular-nums">
            {pct}
            <span className="text-xl text-muted-foreground">%</span>
          </p>
          <div className="h-[3px] bg-foreground/[0.08] overflow-hidden rounded-full mt-4">
            <div
              className="h-full bg-foreground rounded-full transition-[width] duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </>
      )}

      <div className="mt-5">
        <Row label="Faktura" value={progress?.headerName ?? "—"} muted={!progress?.headerName} />
        <Row
          label={indeterminate ? "Stranica" : "Stranice"}
          value={pagesTotal > 0 ? `${pagesDone} / ${pagesTotal}` : "—"}
        />
        <Row label="Pronađeno artikala" value={progress ? String(progress.itemsFound) : "—"} />
        {showEta && <Row label="Preostalo" value={formatEta(etaMs!)} />}
      </div>

      {progress?.failedRanges.map((r) => (
        <p key={`${r.fromPage}-${r.toPage}`} className="text-[10px] text-destructive mt-2">
          Stranice {r.fromPage}–{r.toPage} nisu obrađene
        </p>
      ))}

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
      `}</style>
    </div>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex justify-between items-baseline text-[11px] py-[7px] border-b border-foreground/[0.06] last:border-b-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={`tabular-nums ${muted ? "text-muted-foreground" : "font-medium"} truncate max-w-[60%]`}>
        {value}
      </span>
    </div>
  );
}

/**
 * Memoized: a large invoice can carry 1000+ items, and without this every
 * keystroke in the Naziv field re-renders the whole list. `items` keeps its
 * identity across those edits (setOcrResult spreads the object, not the array),
 * so the memo actually holds.
 */
const OcrItemsList = memo(function OcrItemsList({ items }: { items: any[] }) {
  return (
    <div className="max-h-56 overflow-y-auto overflow-x-hidden">
      {items.map((item: any, i: number) => (
        <div
          key={i}
          className="px-4 py-2 flex items-center gap-3 border-t border-foreground/[0.04] first:border-0"
        >
          <span className="text-[10px] tabular-nums text-muted-foreground/50 w-6 text-right shrink-0">
            {i + 1}
          </span>
          <div className="flex-1 min-w-0 flex items-baseline gap-1.5">
            <span className="text-[11px] font-semibold tabular-nums shrink-0">{item.vendor_code}</span>
            {/* truncate needs a block-level box — on an inline span it does nothing,
                so a long article name used to spill out of the row. */}
            <span className="text-[11px] text-muted-foreground truncate min-w-0">{item.name}</span>
          </div>
          <span className="text-[11px] font-semibold tabular-nums shrink-0">
            {item.quantity}
          </span>
        </div>
      ))}
    </div>
  );
});

function InvoiceSection({ title, invoices, onDelete }: { title: string; invoices: any[]; onDelete: (id: string, e: React.MouseEvent) => void }) {
  return (
    <div>
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-medium">{title}</span>
      <div className="mt-1.5 flex flex-col gap-px bg-foreground/[0.03]">
        {invoices.map((inv: any) => {
          const cfg = STATUS_CONFIG[inv.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.OPEN;
          const Icon = cfg.icon;
          return (
            <Link
              key={inv.id}
              to="/warehouse/brojanje-robe/$invoiceId"
              params={{ invoiceId: inv.id }}
              className="group flex items-center gap-3 px-3 py-2.5 bg-background hover:bg-muted/40 transition-colors"
            >
              <div className={`shrink-0 ${cfg.class}`}>
                <Icon className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium truncate block">{inv.name}</span>
                <span className="text-[11px] text-muted-foreground tabular-nums">
                  {new Date(inv.date).toLocaleDateString("hr")}
                </span>
              </div>
              <button
                onClick={(e) => onDelete(inv.id, e)}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:text-destructive text-muted-foreground"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
